import { type AxiosInstance, type AxiosResponse } from 'axios';
import { type DatafeedConfiguration, type LibrarySymbolInfo } from 'charting_library';

import { dateHeaderToTimestamp } from '#/shared/utils/date-header-to-timestamp';

import { type HmrSchedule } from '#/types/hmr.types';
import { type CandlesUdfResponse, type TicksUdfResponse, type UdfErrorResponse } from '#/types/udf.types';

import {
  type HistoryUrlParams,
  type HmrScheduleHistoryParams,
  type IApiProvider,
  type SymbolsUrlParams,
} from '../api-provider.types';

export class CfdProvider implements IApiProvider {
  readonly #providerName = 'CfdProvider';

  readonly #candlesHistoryBaseUrl = '/api/v1/candles_history';
  readonly #ticksHistoryBaseUrl = '/api/v1/ticks_history';
  readonly #leverageSettingsBaseUrl = '/api/v1/leverage_settings';

  readonly #client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.#client = client;
  }

  async serverTime() {
    const response = await this.#client.get<never, { headers: Record<string, string | undefined> }>(
      '/api/v1/service_status/health'
    );

    return dateHeaderToTimestamp(response.headers, this.#providerName);
  }

  async config() {
    return this.#client.get<never, AxiosResponse<DatafeedConfiguration>>(`${this.#candlesHistoryBaseUrl}/config`);
  }

  async symbols({ urlParams }: { urlParams: SymbolsUrlParams }) {
    return this.#client.get<never, AxiosResponse<LibrarySymbolInfo | UdfErrorResponse | null>>(
      `${this.#candlesHistoryBaseUrl}/symbols`,
      { params: urlParams }
    );
  }

  async candlesHistory({ urlParams }: { urlParams: HistoryUrlParams }) {
    return this.#client.get<never, AxiosResponse<CandlesUdfResponse>>(`${this.#candlesHistoryBaseUrl}/history`, {
      params: urlParams,
    });
  }

  async ticksHistory({ urlParams }: { urlParams: HistoryUrlParams }) {
    return this.#client.get<never, AxiosResponse<TicksUdfResponse>>(`${this.#ticksHistoryBaseUrl}/history`, {
      params: urlParams,
    });
  }

  async hmrScheduleHistory({ urlParams }: { urlParams: HmrScheduleHistoryParams }) {
    return this.#client.get<never, AxiosResponse<HmrSchedule[]>>(
      `${this.#leverageSettingsBaseUrl}/hmr_schedule_history`,
      {
        params: {
          symbol: urlParams.symbol,
          symbol_group: urlParams.symbolGroup,
          account_number: urlParams.accountNumber,
          account_group: urlParams.accountGroup,
        },
      }
    );
  }
}
